import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Play, Save, X } from "lucide-react";
import { apiErrorMessage } from "../../api";

// Convert human time strings to minutes from midnight.
// Accepts: "9", "9:00", "09:30", "9am", "9 am", "9:15am", "9:15 PM", "17:00", "5pm",
// and compact military "800" / "0800" / "1230" / "2359".
function parseTime(s) {
  if (!s) return null;
  const raw = s.trim().toLowerCase().replace(/\s+/g, "");
  if (!raw) return null;

  // Compact military form (3-4 digits, no colon, no am/pm) — e.g. 800, 0800, 1230
  const compact = /^(\d{3,4})$/.exec(raw);
  if (compact) {
    const digits = compact[1];
    const mm = parseInt(digits.slice(-2), 10);
    const h = parseInt(digits.slice(0, -2), 10);
    if (Number.isNaN(h) || Number.isNaN(mm)) return null;
    if (h < 0 || h > 23 || mm < 0 || mm > 59) return null;
    return h * 60 + mm;
  }

  const m = /^(\d{1,2})(?::(\d{2}))?(am|pm)?$/.exec(raw);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const mm = m[2] ? parseInt(m[2], 10) : 0;
  const ap = m[3];
  if (Number.isNaN(h) || Number.isNaN(mm)) return null;
  if (mm < 0 || mm > 59) return null;
  if (ap) {
    if (h < 1 || h > 12) return null;
    if (ap === "am" && h === 12) h = 0;
    else if (ap === "pm" && h !== 12) h += 12;
  } else {
    if (h < 0 || h > 23) return null;
  }
  return h * 60 + mm;
}
function fmtDuration(mins) {
  if (mins == null || mins <= 0) return "0:00";
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return `${h}:${String(m).padStart(2, "0")}`;
}
// Accepts "1.5" (decimal hours), "1:30" (H:MM), or "90m" / "45min" (minutes). Returns minutes or 0.
function parseDurationToMins(s) {
  if (!s) return 0;
  const raw = String(s).trim().toLowerCase();
  if (!raw) return 0;
  const hm = /^(\d+):(\d{1,2})$/.exec(raw);
  if (hm) return parseInt(hm[1], 10) * 60 + parseInt(hm[2], 10);
  const minsOnly = /^(\d+(?:\.\d+)?)\s*(?:m|min|mins|minutes)$/.exec(raw);
  if (minsOnly) return Math.round(parseFloat(minsOnly[1]));
  const dec = Number(raw);
  if (!Number.isNaN(dec) && dec > 0) return Math.round(dec * 60);
  return 0;
}
function humanDate(iso) {
  try {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short" });
  } catch { return iso; }
}
function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

/**
 * New time entry dialog — mirrors Harvest's "New time entry" popup.
 * Props:
 *   open, onClose
 *   projects: [{id, name, client_id, billable}]
 *   clients: [{id, name, color}]
 *   projectTasks: { [projectId]: [{ id, task_id, task_name, effective_billable }] }
 *   fallbackTasks: [string] — used when the project has no tasks attached yet
 *   initialDate: "YYYY-MM-DD" (defaults to today)
 *   canStartTimer: boolean (false if a timer is already running)
 *   onStartTimer({project_id, task, task_id, notes}) => void — closes dialog after
 *   onSaveEntry({project_id, task, task_id, notes, date, hours}) => Promise
 */
export default function NewEntryDialog({
  open, onClose,
  projects = [], clients = [], projectTasks = {}, fallbackTasks = [],
  initialDate,
  canStartTimer = true,
  onStartTimer, onSaveEntry,
}) {
  const [projectId, setProjectId] = useState("");
  const [taskId, setTaskId] = useState("");
  const [taskName, setTaskName] = useState("");
  const [notes, setNotes] = useState("");
  const [date, setDate] = useState(initialDate || todayIso());
  const [startStr, setStartStr] = useState("");
  const [endStr, setEndStr] = useState("");
  const [manualHours, setManualHours] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  // Reset when dialog opens.
  useEffect(() => {
    if (!open) return;
    setNotes("");
    setDate(initialDate || todayIso());
    setStartStr(""); setEndStr(""); setManualHours("");
    setErr("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialDate]);

  // Initialise project default once data arrives
  useEffect(() => {
    if (!open) return;
    if (!projectId && projects.length) setProjectId(projects[0].id);
  }, [open, projects, projectId]);

  // Tasks available for the selected project
  const availableTasks = useMemo(() => {
    if (!projectId) return [];
    return (projectTasks[projectId] || []).filter((l) => l.is_active !== false);
  }, [projectId, projectTasks]);

  // When the project changes, reset the task selection to that project's first task
  useEffect(() => {
    if (!open) return;
    if (availableTasks.length > 0) {
      setTaskId(availableTasks[0].task_id);
      setTaskName(availableTasks[0].task_name);
    } else {
      setTaskId("");
      setTaskName(fallbackTasks[0] || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, availableTasks.length]);

  // Auto-compute duration from start/end, or from manual entry
  const durationMins = useMemo(() => {
    const manual = parseDurationToMins(manualHours);
    if (manual > 0) return manual;
    const s = parseTime(startStr);
    const e = parseTime(endStr);
    if (s == null || e == null) return 0;
    return Math.max(0, e - s);
  }, [startStr, endStr, manualHours]);

  const projectsByClient = useMemo(() => {
    const map = new Map();
    for (const p of projects) {
      const cid = p.client_id || "__none";
      if (!map.has(cid)) map.set(cid, []);
      map.get(cid).push(p);
    }
    return map;
  }, [projects]);

  const selectedProject = projects.find((p) => p.id === projectId);
  const hasTasks = availableTasks.length > 0;
  const currentTaskName = hasTasks
    ? (availableTasks.find((l) => l.task_id === taskId)?.task_name || "")
    : taskName;
  const canStart = !!projectId && !!currentTaskName && canStartTimer;
  const canSave = !!projectId && !!currentTaskName && durationMins > 0;

  const doStart = async () => {
    if (!canStart) return;
    try {
      await onStartTimer?.({ project_id: projectId, task: currentTaskName, task_id: hasTasks ? taskId : null, notes });
      onClose?.();
    } catch (e) {
      setErr(e?.message || "Could not start timer");
    }
  };

  const doSave = async () => {
    if (!canSave || saving) return;
    setSaving(true); setErr("");
    try {
      const hours = Math.round((durationMins / 60) * 100) / 100;
      await onSaveEntry?.({
        project_id: projectId, task: currentTaskName,
        task_id: hasTasks ? taskId : null,
        notes, date, hours,
      });
      onClose?.();
    } catch (e) {
      setErr(apiErrorMessage(e, "Could not save entry"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="sm:max-w-[560px] bg-white" data-testid="new-entry-dialog">
        <DialogHeader>
          <DialogTitle className="text-center">
            New time entry for {humanDate(date)}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Pick a project and task, then start a timer or enter a duration to log an entry.
          </DialogDescription>
        </DialogHeader>

        {projects.length === 0 ? (
          <div className="py-6 text-sm text-forest/70 text-center">
            You have no projects yet. Create one on the{" "}
            <a href="/app/projects" className="text-coral font-medium">Projects</a> page first.
          </div>
        ) : (
          <div className="space-y-3 py-1">
            {/* Project / Task */}
            <div>
              <label className="text-xs font-semibold text-forest">Project / Task</label>
              <select
                data-testid="new-entry-project"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                className="w-full mt-1 border border-cream rounded-md px-3 py-2 text-sm bg-white"
              >
                {[...projectsByClient.entries()].map(([cid, list]) => {
                  const c = clients.find((cc) => cc.id === cid);
                  const label = c?.name || "No client";
                  return (
                    <optgroup key={cid} label={label}>
                      {list.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </optgroup>
                  );
                })}
              </select>
              {selectedProject && (
                <div className="text-[11px] text-forest/50 mt-1">
                  {clients.find((c) => c.id === selectedProject.client_id)?.name || "No client"}
                </div>
              )}
            </div>

            <div>
              {hasTasks ? (
                <select
                  data-testid="new-entry-task"
                  value={taskId}
                  onChange={(e) => setTaskId(e.target.value)}
                  className="w-full border border-cream rounded-md px-3 py-2 text-sm bg-white"
                >
                  {availableTasks.map((l) => (
                    <option key={l.task_id} value={l.task_id}>
                      {l.task_name}{l.effective_billable ? "" : " · non-billable"}
                    </option>
                  ))}
                </select>
              ) : (
                <>
                  <select
                    data-testid="new-entry-task"
                    value={taskName}
                    onChange={(e) => setTaskName(e.target.value)}
                    className="w-full border border-cream rounded-md px-3 py-2 text-sm bg-white"
                  >
                    {fallbackTasks.length === 0 && <option value="">(no tasks yet)</option>}
                    {fallbackTasks.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <div className="mt-1 text-[10px] text-coral" data-testid="no-tasks-hint">
                    No tasks attached to this project yet — <a href="/app/projects" className="underline">attach some</a> so your team picks from a shared list.
                  </div>
                </>
              )}
            </div>

            <div>
              <textarea
                data-testid="new-entry-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Notes (optional)"
                rows={2}
                className="w-full border border-cream rounded-md px-3 py-2 text-sm bg-white resize-none"
              />
            </div>

            {/* Date + duration row */}
            <div className="flex items-center gap-2">
              <input
                data-testid="new-entry-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="border border-cream rounded-md px-2 py-2 text-sm bg-white"
              />
              <input
                data-testid="new-entry-start"
                type="text"
                value={startStr}
                onChange={(e) => { setStartStr(e.target.value); setManualHours(""); }}
                placeholder="Start time"
                className="border border-cream rounded-md px-2 py-2 text-sm w-24 bg-white"
              />
              <span className="text-xs text-forest/60">to</span>
              <input
                data-testid="new-entry-end"
                type="text"
                value={endStr}
                onChange={(e) => { setEndStr(e.target.value); setManualHours(""); }}
                placeholder="End time"
                className="border border-cream rounded-md px-2 py-2 text-sm w-24 bg-white"
              />
              <span className="text-xs text-forest/60">=</span>
              <input
                data-testid="new-entry-hours"
                type="text"
                value={manualHours || fmtDuration(durationMins)}
                onChange={(e) => {
                  const next = e.target.value;
                  // Only clear start/end when the user begins entering a manual duration
                  // (transition from empty -> non-empty). Backspacing back to empty won't wipe them.
                  if (next && !manualHours) { setStartStr(""); setEndStr(""); }
                  setManualHours(next);
                }}
                placeholder="0:00"
                className="border border-cream rounded-md px-2 py-2 text-sm w-20 bg-white tabular-nums"
              />
            </div>
            <div className="text-[11px] text-forest/50">
              Fill start/end times (or a duration like <span className="tabular-nums">1:30</span>) and press
              <span className="font-semibold text-forest"> Enter time</span> to log manually, or leave times blank and press
              <span className="font-semibold text-forest"> Start timer</span> to track right now.
            </div>

            {err && (
              <div className="text-sm text-red-600" data-testid="new-entry-error">{err}</div>
            )}
          </div>
        )}

        <DialogFooter className="flex flex-col sm:flex-row gap-2 pt-2">
          {projects.length > 0 && (
            <>
              <button
                data-testid="new-entry-save"
                onClick={doSave}
                disabled={!canSave || saving}
                className="btn-coral px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                title={!canSave ? "Fill start/end times or a duration first" : ""}
              >
                <Save size={14} /> {saving ? "Saving…" : "Enter time"}
              </button>
              <button
                data-testid="new-entry-start-timer"
                onClick={doStart}
                disabled={!canStart || durationMins > 0}
                className="btn-outline-forest px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                title={
                  !canStartTimer
                    ? "A timer is already running"
                    : durationMins > 0
                    ? "Clear the duration to start a live timer instead"
                    : ""
                }
              >
                <Play size={14} /> Start timer
              </button>
            </>
          )}
          <button
            data-testid="new-entry-cancel"
            onClick={onClose}
            className="btn-outline-forest px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 sm:ml-auto"
          >
            <X size={14} /> Cancel
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
